export type MoneyInputOpts = {
  allowNegative?: boolean
}

function roundToCents(n: number) {
  return Math.round(n * 100) / 100
}

function isNegativeInput(raw: string, allowNegative: boolean) {
  if (!allowNegative) return false
  const t = String(raw || '').trim()
  if (t.includes('(') && t.includes(')')) return true
  return /^\s*-/.test(t.replace('$', ''))
}

/** Live display while typing: `$1,234.5`, `-$60`. */
export function formatMoneyTyping(raw: string, opts?: MoneyInputOpts): string {
  const allowNegative = opts?.allowNegative !== false
  const source = String(raw ?? '')
  const negative = isNegativeInput(source, allowNegative)
  const cleaned = source.replace(/[^0-9.]/g, '')
  const dot = cleaned.indexOf('.')
  let intDigits = (dot === -1 ? cleaned : cleaned.slice(0, dot)).replace(/^0+(?=\d)/, '')
  const frac = dot === -1 ? null : cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2)

  if (!intDigits && frac == null) return negative ? '-' : ''
  if (!intDigits) intDigits = '0'

  const grouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  let out = `$${grouped}`
  if (frac != null) out += `.${frac}`
  return negative ? `-${out}` : out
}

export function formatMoneyDisplay(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(roundToCents(n))
}

export function moneyInputToNumber(raw: string, opts?: MoneyInputOpts): number | null {
  const allowNegative = opts?.allowNegative !== false
  const t = String(raw ?? '').trim()
  if (!t || t === '-' || t === '-$' || t === '$' || t === '$-') return null
  const negative = isNegativeInput(t, allowNegative)
  const digits = t.replace(/[^0-9.]/g, '')
  if (!digits || digits === '.') return null
  const n = Number(digits)
  if (!Number.isFinite(n)) return null
  return roundToCents(negative ? -n : n)
}

/** Numeric string for form state (`"50.5"`, `"-60"`, `""`, or `"-"` while typing a sign). */
export function moneyInputToNumericString(raw: string, opts?: MoneyInputOpts): string {
  const n = moneyInputToNumber(raw, opts)
  if (n != null) return String(n)
  const t = String(raw ?? '').trim()
  if (t === '-' || t === '-$') return '-'
  return ''
}

export function numericStringToMoneyDisplay(value: string, opts?: { allowEmpty?: boolean }): string {
  const t = String(value ?? '').trim()
  if (!t) return opts?.allowEmpty === false ? formatMoneyDisplay(0) : ''
  if (t === '-') return '-'
  const n = Number(t)
  if (!Number.isFinite(n)) return ''
  return formatMoneyDisplay(n)
}
